import { type EbayCategoryPolicy, type EbayItemSpecifics } from '@tcvs/shared';
import { validateCcgLocalSpecifics, type CcgFieldDecision } from '../marketplace/ccg-review-state';
export function CcgItemDetails({
  policy,
  value,
  onChange,
  disabled = false,
  fieldName,
  currentResearch,
  draftResearch,
  onUseResearch,
  decisions,
}: {
  policy: EbayCategoryPolicy;
  value: EbayItemSpecifics;
  onChange: (value: EbayItemSpecifics) => void;
  disabled?: boolean;
  fieldName?: string;
  currentResearch?: Partial<Record<'Finish' | 'Rarity', string>>;
  draftResearch?: Partial<Record<'Finish' | 'Rarity', string>>;
  onUseResearch?: (name: 'Finish' | 'Rarity', value: string) => void;
  decisions?: Partial<Record<'Finish' | 'Rarity', CcgFieldDecision>>;
}): JSX.Element {
  const set = (name: string, values: string[]) => {
    const next = { ...value };
    if (values.length === 0) delete next[name];
    else next[name] = values;
    onChange(next);
  };
  let errors: string[];
  try {
    errors = validateCcgLocalSpecifics(policy, value);
  } catch {
    errors = ['Reload the current CCG policy before editing.'];
  }
  return (
    <section aria-label="Reviewed CCG item details">
      <p>
        Game must be explicitly reviewed. Suggestions are examples for free text. Dependencies are
        checked by the server when you save.
      </p>
      {policy.aspects
        .filter((a) => fieldName === undefined || a.name === fieldName)
        .map((a) => {
          const values = value[a.name] ?? [];
          return (
            <div className="tcvs-item-specific-field" key={a.name}>
              <strong>
                {a.name}
                {a.required ? ' (required)' : ''}
              </strong>
              {a.mode === 'SELECTION_ONLY' ? (
                <label>
                  <span>{a.name}</span>
                  <select
                    aria-label={a.name}
                    disabled={disabled}
                    multiple={a.cardinality === 'MULTI'}
                    value={a.cardinality === 'MULTI' ? values : (values[0] ?? '')}
                    onChange={(e) =>
                      set(
                        a.name,
                        Array.from(e.target.selectedOptions)
                          .map((o) => o.value)
                          .filter(Boolean),
                      )
                    }
                  >
                    {a.cardinality === 'SINGLE' ? <option value="">Choose {a.name}</option> : null}
                    {a.selectionValues?.map((v) => (
                      <option key={v} value={v}>
                        {v}
                      </option>
                    ))}
                  </select>
                </label>
              ) : (
                <>
                  {(values.length ? values : ['']).map((v, i) => (
                    <label key={i}>
                      <span>
                        {a.name}
                        {a.cardinality === 'MULTI' ? ` value ${i + 1}` : ''}
                      </span>
                      <input
                        aria-label={a.cardinality === 'MULTI' ? `${a.name} value ${i + 1}` : a.name}
                        value={v}
                        disabled={disabled}
                        list={`ccg-${a.name}`}
                        onChange={(e) => {
                          const next = [...values];
                          next[i] = e.target.value;
                          set(
                            a.name,
                            e.target.value === '' ? next.filter((_, n) => n !== i) : next,
                          );
                        }}
                      />
                    </label>
                  ))}
                  <datalist id={`ccg-${a.name}`}>
                    {a.suggestions.map((v) => (
                      <option key={v} value={v} />
                    ))}
                  </datalist>
                  {a.cardinality === 'MULTI' ? (
                    <button
                      type="button"
                      disabled={disabled || values.length >= 30 || values.at(-1) === ''}
                      onClick={() => set(a.name, [...values, ''])}
                    >
                      Add {a.name} value
                    </button>
                  ) : null}
                </>
              )}
              <small>
                {a.mode === 'FREE_TEXT' ? 'Free text' : 'Choose supported values'} ·{' '}
                {a.cardinality === 'SINGLE' ? 'One value' : 'Up to 30 values'} ·{' '}
                {a.maxLength ?? 200} characters per value
              </small>
              {a.name === 'Finish' || a.name === 'Rarity' ? (
                <>
                  <p>
                    Saved/local value: {values.join(', ') || 'None'}.{' '}
                    {decisions?.[a.name]?.confirmed && decisions[a.name]?.value === values[0]
                      ? 'Explicitly reviewed from current Card research.'
                      : 'Manual details are reviewed by the final Save or Review and queue action.'}
                  </p>
                  {currentResearch?.[a.name as 'Finish' | 'Rarity'] !== undefined ? (
                    <p>
                      Current Card research {a.name}:{' '}
                      {currentResearch![a.name as 'Finish' | 'Rarity']!}{' '}
                      <button
                        type="button"
                        disabled={disabled || !onUseResearch}
                        onClick={() =>
                          onUseResearch?.(
                            a.name as 'Finish' | 'Rarity',
                            currentResearch![a.name as 'Finish' | 'Rarity']!,
                          )
                        }
                      >
                        Use this {a.name}
                      </button>
                    </p>
                  ) : (
                    <p>No current Card research {a.name} suggestion.</p>
                  )}
                  {draftResearch?.[a.name as 'Finish' | 'Rarity'] ? (
                    <p>
                      Unverified draft import research {a.name}:{' '}
                      {draftResearch![a.name as 'Finish' | 'Rarity']}. Review and type this value
                      manually if appropriate; it is not current Card research.
                    </p>
                  ) : null}
                </>
              ) : null}
            </div>
          );
        })}
      {errors.length ? (
        <ul aria-label="CCG detail corrections">
          {errors.map((e, i) => (
            <li key={i}>{e}</li>
          ))}
        </ul>
      ) : null}
    </section>
  );
}
